// src/components/resources/tools/generators/AdSafeZone/safe-zones.ts

/**
 * @fileoverview Pure data + geometry for the Ad Safe Zone Checker.
 *
 * Everything here is deterministic and DOM-free so the margin math the tool
 * renders (and the numbers quoted in its FAQ/reference-table copy) is
 * unit-tested in isolation — see `__tests__/safe-zones.test.ts`.
 *
 * SOURCING RULES (do not edit numbers without updating the citation):
 * Every platform's margins carry a `source` record that names where the
 * numbers come from and how trustworthy they are:
 *
 * - `official-percent` — the platform publishes the margins itself as
 *   percentages of the creative. (Meta Stories/Reels.)
 * - `official-template` — the platform publishes a pixel-accurate safe-zone
 *   template file rather than prose numbers; margins are MEASURED directly
 *   from that artifact (both the TikTok and YouTube templates were downloaded
 *   and measured programmatically on 2026-07-11 — exact bounds in each spec's
 *   doc comment below).
 *
 * Where a platform publishes nothing exact, we do NOT invent margins — the
 * spec's `caveat` says so and the UI labels the overlay approximate.
 */

/** The 9:16 reference canvas every platform spec is expressed against. */
export const REFERENCE_CANVAS = { width: 1080, height: 1920 } as const

/** Aspect ratio (width / height) of the 9:16 vertical placement. */
export const NINE_SIXTEEN = REFERENCE_CANVAS.width / REFERENCE_CANVAS.height

export type PlatformId =
  | 'meta-stories'
  | 'meta-reels'
  | 'tiktok'
  | 'youtube-shorts'

/** How the margins were sourced — drives the badge rendered next to each spec. */
export type SourceKind = 'official-percent' | 'official-template'

export interface ZoneSource {
  /** Short human label for the publisher, e.g. "Meta Business Help Center". */
  publisher: string
  /** Document title as published. */
  title: string
  /** Public URL of the spec/help page the numbers come from. */
  url: string
  kind: SourceKind
}

/**
 * Per-side margins expressed as FRACTIONS of the canvas dimension they cut
 * into (top/bottom → fraction of height; left/right → fraction of width).
 * Fractions, not px, because Meta publishes percentages and every UI surface
 * scales; px values are derived against {@link REFERENCE_CANVAS} for display.
 */
export interface SafeZoneMargins {
  top: number
  bottom: number
  left: number
  right: number
}

/** Human labels for what the platform UI draws over each covered side. */
export interface ZoneLabels {
  top?: string
  bottom?: string
  left?: string
  right?: string
}

/**
 * A covered rectangle that is NOT a full edge band — e.g. TikTok's
 * like/comment/share rail, which its official template draws only over the
 * lower-right portion of the frame (inside the side margins). Fractions of
 * the canvas, like {@link OverlayZone}.
 */
export interface RailZone {
  label: string
  x: number
  y: number
  width: number
  height: number
}

export interface PlatformSafeZoneSpec {
  id: PlatformId
  /** Full display name, e.g. "Meta Reels (Facebook & Instagram)". */
  name: string
  /** Short name for the segmented control, e.g. "Reels". */
  shortName: string
  margins: SafeZoneMargins
  /** Additional covered rect(s) beyond the four edge bands (action rails). */
  rail?: RailZone
  labels: ZoneLabels
  source: ZoneSource
  /** Honest limitation of the numbers, rendered next to the spec. */
  caveat?: string
}

/**
 * Platform specs, most-asked-about first.
 *
 * Meta (Stories + Reels): percentages published by Meta itself in
 * "About text overlays and the Safe Zone for ads in Stories and Reels",
 * Meta Business Help Center — https://www.facebook.com/business/help/980593475366490
 * (Stories: keep the top 14% and bottom 20% clear; Reels: top 14%,
 * bottom 35%, and 6% on each side).
 *
 * TikTok: TikTok publishes pixel-accurate safe-zone template files (the
 * margins vary with caption length and interactive add-ons, so prose numbers
 * aren't published) — "TikTok Auction In-Feed Ads", TikTok Ads Manager Help
 * Center, https://ads.tiktok.com/help/article/tiktok-auction-in-feed-ads.
 * Measured directly from the official "In-Feed-Standard Version LTR" zip
 * linked on that page (Feed.png, 2880×5120 = 4× the template's annotated
 * 720×1280 base; downloaded + measured 2026-07-11). The template's own
 * annotations and the measured transparent (safe) region agree exactly:
 * on 720×1280 — 160 px top, 440 px bottom, 80 px on each side (marked as
 * device-crop margins), PLUS a 120×280 px engagement-rail block on the
 * lower right (x ∈ [520, 640], y ∈ [560, 840], sitting between the side
 * margin and the bottom band). Scaled to 1080×1920 that is 240 top, 660
 * bottom, 120 left/right, and a 180×420 rail at x 780, y 840.
 *
 * YouTube Shorts: measured directly from Google's published vertical
 * safe-zone overlay PNG (linked from "About video ad specs", Google Ads Help,
 * https://support.google.com/google-ads/answer/13547298 →
 * https://services.google.com/fh/files/misc/youtubesafezoneoverlay_vertical_final.png).
 * The overlay is 1080×1920 with a fully transparent safe rectangle at
 * x ∈ [48, 887], y ∈ [288, 1247] — i.e. an 840×960 safe area → margins
 * 48 px left, 192 px right, 288 px top, 672 px bottom (measured 2026-07).
 */
export const PLATFORM_SAFE_ZONES: readonly PlatformSafeZoneSpec[] = [
  {
    id: 'tiktok',
    name: 'TikTok In-Feed',
    shortName: 'TikTok',
    // 720×1280 template base: top 160, bottom 440, sides 80 (see file header).
    margins: {
      top: 160 / 1280,
      bottom: 440 / 1280,
      left: 80 / 720,
      right: 80 / 720,
    },
    // The template's 120×280 engagement-rail block (x 520, y 560 on 720×1280),
    // adjacent to the right margin and the bottom band.
    rail: {
      label: 'Like / comment / share rail',
      x: 520 / 720,
      y: 560 / 1280,
      width: 120 / 720,
      height: 280 / 1280,
    },
    labels: {
      top: 'Username, feed tabs & search',
      bottom: 'Caption, CTA button & music ticker',
      left: 'Device-crop margin',
      right: 'Device-crop margin',
    },
    source: {
      publisher: 'TikTok Ads Manager Help Center',
      title: 'TikTok Auction In-Feed Ads — safe zone templates',
      url: 'https://ads.tiktok.com/help/article/tiktok-auction-in-feed-ads',
      kind: 'official-template',
    },
    caveat:
      'Measured from TikTok’s official In-Feed safe-zone template (standard vertical, downloaded 2026-07). TikTok’s covered area grows with caption length and interactive add-ons, so treat this as a minimum and confirm in TikTok’s ad preview.',
  },
  {
    id: 'meta-reels',
    name: 'Meta Reels (Facebook & Instagram)',
    shortName: 'Reels',
    margins: { top: 0.14, bottom: 0.35, left: 0.06, right: 0.06 },
    labels: {
      top: 'Camera & profile controls',
      bottom: 'Caption, audio, CTA & engagement actions',
      left: 'Side margin (Meta safe-zone spec)',
      right: 'Side margin (Meta safe-zone spec)',
    },
    source: {
      publisher: 'Meta Business Help Center',
      title: 'About text overlays and the Safe Zone for ads in Stories and Reels',
      url: 'https://www.facebook.com/business/help/980593475366490',
      kind: 'official-percent',
    },
  },
  {
    id: 'meta-stories',
    name: 'Meta Stories (Facebook & Instagram)',
    shortName: 'Stories',
    margins: { top: 0.14, bottom: 0.2, left: 0, right: 0 },
    labels: {
      top: 'Profile info, timer & close control',
      bottom: 'Call-to-action / swipe-up & reply composer',
    },
    source: {
      publisher: 'Meta Business Help Center',
      title: 'About text overlays and the Safe Zone for ads in Stories and Reels',
      url: 'https://www.facebook.com/business/help/980593475366490',
      kind: 'official-percent',
    },
  },
  {
    id: 'youtube-shorts',
    name: 'YouTube Shorts',
    shortName: 'Shorts',
    margins: {
      top: 288 / 1920,
      bottom: 672 / 1920,
      left: 48 / 1080,
      right: 192 / 1080,
    },
    labels: {
      top: 'Search & camera controls',
      bottom: 'Title, channel, audio & Shorts UI',
      left: 'Edge margin',
      right: 'Like / comment / share / remix rail',
    },
    source: {
      publisher: 'Google Ads Help',
      title: 'About video ad specs — vertical safe-zone overlay',
      url: 'https://support.google.com/google-ads/answer/13547298',
      kind: 'official-template',
    },
    caveat:
      'Measured from Google’s published vertical safe-zone overlay PNG (840×960 px safe area at x 48, y 288 on 1080×1920).',
  },
] as const

/** Lookup by id — the registry above is the single source of truth. */
export function getPlatformSpec(id: PlatformId): PlatformSafeZoneSpec {
  const spec = PLATFORM_SAFE_ZONES.find((s) => s.id === id)
  // The PlatformId union makes a miss unrepresentable from typed callers;
  // guard anyway so an untyped call site fails loudly, not with `undefined`.
  if (!spec) throw new Error(`Unknown safe-zone platform: ${id}`)
  return spec
}

/** A rectangle in the same unit space as the inputs that produced it. */
export interface Rect {
  x: number
  y: number
  width: number
  height: number
}

/**
 * The safe rectangle left after removing the four EDGE-BAND margins from a
 * canvas. This is margins-only — it does NOT model rail blocks (for TikTok's
 * margins alone it yields the 840 px band-only width between the side
 * margins). The user-facing "largest fully safe rectangle" readout uses
 * {@link computeSpecSafeRect}, which additionally clips the rail and reports
 * 660×1020 for TikTok on the 1080×1920 canvas (`r3564178619`).
 */
export function computeSafeRect(
  margins: SafeZoneMargins,
  canvasWidth: number = REFERENCE_CANVAS.width,
  canvasHeight: number = REFERENCE_CANVAS.height,
): Rect {
  const x = margins.left * canvasWidth
  const y = margins.top * canvasHeight
  return {
    x,
    y,
    width: canvasWidth - x - margins.right * canvasWidth,
    height: canvasHeight - y - margins.bottom * canvasHeight,
  }
}

/** Fraction of the canvas area that stays safe (0..1) for plain margins. */
export function safeAreaFraction(margins: SafeZoneMargins): number {
  return (
    (1 - margins.left - margins.right) * (1 - margins.top - margins.bottom)
  )
}

/**
 * A spec's margins widened just enough that the resulting RECTANGLE also
 * excludes the rail — i.e. the margins of the LARGEST safe rectangle.
 *
 * With a rail the true safe region is L-shaped; a rectangle model has to clip
 * on one side of the rail. We evaluate all four clip directions (treat the
 * rail's near edge as a deeper left/right/top/bottom margin) and keep the one
 * with the largest remaining area, so the reported rectangle is the biggest
 * fully-safe layout box. For TikTok that clips left of the rail: the safe
 * rectangle is 660×1020 on 1080×1920 — narrower than the frame, because the
 * rail eats into the right side (rail-aware width was a defect in the first
 * attempt at this tool and is asserted in the unit tests).
 */
export function conservativeMargins(spec: PlatformSafeZoneSpec): SafeZoneMargins {
  const { margins, rail } = spec
  if (!rail) return margins
  // If the rail doesn't actually intrude on the base rectangle, ignore it.
  const intersects =
    rail.x < 1 - margins.right &&
    rail.x + rail.width > margins.left &&
    rail.y < 1 - margins.bottom &&
    rail.y + rail.height > margins.top
  if (!intersects) return margins
  const candidates: SafeZoneMargins[] = [
    { ...margins, right: Math.max(margins.right, 1 - rail.x) },
    { ...margins, left: Math.max(margins.left, rail.x + rail.width) },
    { ...margins, bottom: Math.max(margins.bottom, 1 - rail.y) },
    { ...margins, top: Math.max(margins.top, rail.y + rail.height) },
  ]
  return candidates.reduce((best, candidate) =>
    safeAreaFraction(candidate) > safeAreaFraction(best) ? candidate : best,
  )
}

/**
 * The largest fully-safe RECTANGLE for a spec (rail-aware) in canvas px.
 * This is what the tool reports as "safe area" and outlines in the preview.
 */
export function computeSpecSafeRect(
  spec: PlatformSafeZoneSpec,
  canvasWidth: number = REFERENCE_CANVAS.width,
  canvasHeight: number = REFERENCE_CANVAS.height,
): Rect {
  return computeSafeRect(conservativeMargins(spec), canvasWidth, canvasHeight)
}

/**
 * Fraction of the canvas that platform UI leaves uncovered for a spec —
 * the (possibly L-shaped) TRUE safe area: base rectangle minus the part of
 * the rail that intrudes into it.
 */
export function specSafeAreaFraction(spec: PlatformSafeZoneSpec): number {
  const { margins, rail } = spec
  const base = safeAreaFraction(margins)
  if (!rail) return base
  const overlapW =
    Math.min(rail.x + rail.width, 1 - margins.right) - Math.max(rail.x, margins.left)
  const overlapH =
    Math.min(rail.y + rail.height, 1 - margins.bottom) - Math.max(rail.y, margins.top)
  if (overlapW <= 0 || overlapH <= 0) return base
  return base - overlapW * overlapH
}

/**
 * The strictest combined margins across a set of specs — per-side maxima of
 * each spec's rail-aware {@link conservativeMargins}. One creative laid out
 * inside this rectangle is inside every individual platform's safe zone
 * (including clear of TikTok's rail).
 */
export function combinedMargins(
  specs: readonly PlatformSafeZoneSpec[],
): SafeZoneMargins {
  return specs.reduce<SafeZoneMargins>(
    (acc, spec) => {
      const margins = conservativeMargins(spec)
      return {
        top: Math.max(acc.top, margins.top),
        bottom: Math.max(acc.bottom, margins.bottom),
        left: Math.max(acc.left, margins.left),
        right: Math.max(acc.right, margins.right),
      }
    },
    { top: 0, bottom: 0, left: 0, right: 0 },
  )
}

/** One rendered overlay band, in canvas fractions (0..1). */
export interface OverlayZone {
  side: 'top' | 'bottom' | 'left' | 'right'
  label: string
  x: number
  y: number
  width: number
  height: number
}

/**
 * The overlay bands for a set of margins: full-width top/bottom bands plus
 * left/right bands spanning only the space BETWEEN them (so corners aren't
 * double-covered). Zero-size sides are omitted.
 */
export function overlayZones(
  margins: SafeZoneMargins,
  labels: ZoneLabels = {},
): OverlayZone[] {
  const zones: OverlayZone[] = []
  const midY = margins.top
  const midHeight = 1 - margins.top - margins.bottom
  if (margins.top > 0) {
    zones.push({
      side: 'top',
      label: labels.top ?? 'Platform UI (top)',
      x: 0,
      y: 0,
      width: 1,
      height: margins.top,
    })
  }
  if (margins.bottom > 0) {
    zones.push({
      side: 'bottom',
      label: labels.bottom ?? 'Platform UI (bottom)',
      x: 0,
      y: 1 - margins.bottom,
      width: 1,
      height: margins.bottom,
    })
  }
  if (margins.left > 0) {
    zones.push({
      side: 'left',
      label: labels.left ?? 'Platform UI (left)',
      x: 0,
      y: midY,
      width: margins.left,
      height: midHeight,
    })
  }
  if (margins.right > 0) {
    zones.push({
      side: 'right',
      label: labels.right ?? 'Platform UI (right)',
      x: 1 - margins.right,
      y: midY,
      width: margins.right,
      height: midHeight,
    })
  }
  return zones
}

/**
 * All rendered overlay zones for a spec: the four edge bands plus the rail
 * block (if any), so an action rail that sits INSIDE the side margins (like
 * TikTok's) is drawn where the platform actually draws it instead of being
 * smeared into a full-height edge band.
 */
export function specZones(spec: PlatformSafeZoneSpec): OverlayZone[] {
  const zones = overlayZones(spec.margins, spec.labels)
  if (spec.rail) {
    zones.push({ side: 'right', ...spec.rail })
  }
  return zones
}

/** How a non-9:16 creative is placed on the 9:16 stage. */
export type FitMode = 'fit' | 'fill'

/**
 * Place an image inside a frame:
 * - `fit` (contain / letterbox) — the WHOLE creative stays visible, centered,
 *   with bars on the short axis. The honest default: nothing is hidden.
 * - `fill` (cover / center-crop) — the frame is filled and overflow is
 *   cropped equally on both sides, simulating how a platform center-crops a
 *   mismatched upload. Only ever offered as an explicit, labeled choice.
 *
 * Returns the image rect in frame coordinates (may have negative x/y and
 * exceed the frame in `fill` mode).
 */
export function fitRect(
  imageWidth: number,
  imageHeight: number,
  frameWidth: number,
  frameHeight: number,
  mode: FitMode,
): Rect {
  if (imageWidth <= 0 || imageHeight <= 0 || frameWidth <= 0 || frameHeight <= 0) {
    return { x: 0, y: 0, width: 0, height: 0 }
  }
  const scaleX = frameWidth / imageWidth
  const scaleY = frameHeight / imageHeight
  const scale = mode === 'fit' ? Math.min(scaleX, scaleY) : Math.max(scaleX, scaleY)
  const width = imageWidth * scale
  const height = imageHeight * scale
  return {
    x: (frameWidth - width) / 2,
    y: (frameHeight - height) / 2,
    width,
    height,
  }
}

/**
 * Whether a creative is (within tolerance) the 9:16 vertical aspect.
 * Default tolerance: 1% relative deviation of the width/height ratio — wide
 * enough to absorb encoder rounding (e.g. 1080×1918), tight enough that 4:5
 * or 1:1 uploads are flagged.
 */
export function isNineBySixteen(
  width: number,
  height: number,
  tolerance = 0.01,
): boolean {
  if (width <= 0 || height <= 0) return false
  const ratio = width / height
  return Math.abs(ratio - NINE_SIXTEEN) / NINE_SIXTEEN <= tolerance
}

/**
 * Human aspect description, e.g. "16:9" for 1920×1080 or "1080:1917" when no
 * small integer ratio exists. Used in the explicit aspect-mismatch notice.
 */
export function describeAspect(width: number, height: number): string {
  if (width <= 0 || height <= 0) return '—'
  const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b))
  const d = gcd(Math.round(width), Math.round(height))
  return `${Math.round(width) / d}:${Math.round(height) / d}`
}

/** Round a fraction of a reference dimension to whole display pixels. */
export function toReferencePx(fraction: number, dimension: number): number {
  return Math.round(fraction * dimension)
}

/**
 * Client-side upload guards. 10 MB matches the shared generator upload cap
 * (`shared/uploadLimits.ts` — re-exported by the component); the pixel budget
 * exists because decode cost scales with AREA, not file size: a 10 MB PNG can
 * decode to hundreds of MB of bitmap and freeze the tab. 48 MP comfortably
 * admits every real ad export (a 4× retina 9:16 at 4320×7680 is 33 MP) while
 * rejecting the pathological cases.
 */
export const MAX_IMAGE_PIXELS = 48_000_000
